import { useEffect, useRef, useState } from "react";
import { X, Play, Pause, Volume2, VolumeX, Loader2 } from "lucide-react";
import { Button } from "./ui/button";
import { GULF_VIDEO_URL, GULF_VIDEO_LABEL } from "../lib/gulfConstants";
import { trackVideo, trackCtaClick } from "../lib/api";
import { priceDisplay } from "../lib/format";
import { useGeo } from "../lib/useGeo";

/** Gulf video modal — mirrors EgVideoModal but uses Gulf geo-pricing
 *  (50 ريال / 55 درهم / $27) inside the under-video buy CTA. */
export default function GulfVideoModal({ open, onClose, onBuy, ctaLabel = "gulf-video-modal-buy" }) {
  const videoRef = useRef(null);
  const milestonesFired = useRef(new Set());
  const country = useGeo();
  const main = priceDisplay(27.0, country);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [progressPct, setProgressPct] = useState(0);

  useEffect(() => {
    if (open) {
      document.body.style.overflow = "hidden";
      milestonesFired.current = new Set();
      setProgressPct(0);
      setPlaying(false);
    } else {
      document.body.style.overflow = "";
      const v = videoRef.current;
      if (v) { try { v.pause(); } catch (_) { /* ignore */ } }
    }
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { onClose(); return; }
      const blocked = ["ArrowLeft","ArrowRight","ArrowUp","ArrowDown"," ","Spacebar","j","k","l","f","m","0","1","2","3","4","5","6","7","8","9","Home","End"];
      if (blocked.includes(e.key)) { e.preventDefault(); e.stopPropagation(); }
    };
    window.addEventListener("keydown", onKey, true);
    return () => window.removeEventListener("keydown", onKey, true);
  }, [open, onClose]);

  const handlePlayPause = () => {
    const v = videoRef.current; if (!v) return;
    if (v.paused) v.play().catch(() => {}); else v.pause();
  };

  const handleTimeUpdate = () => {
    const v = videoRef.current;
    if (!v || !v.duration || !isFinite(v.duration)) return;
    const pct = (v.currentTime / v.duration) * 100;
    setProgressPct(pct);
    [25, 50, 75].forEach((m) => {
      if (pct >= m && !milestonesFired.current.has(m)) {
        milestonesFired.current.add(m);
        trackVideo("progress", m, GULF_VIDEO_LABEL);
      }
    });
  };

  const handlePlay = () => {
    setPlaying(true);
    if (!milestonesFired.current.has("play")) {
      milestonesFired.current.add("play");
      trackVideo("play", 0, GULF_VIDEO_LABEL);
    }
  };

  const handleEnded = () => {
    setPlaying(false);
    if (!milestonesFired.current.has("complete")) {
      milestonesFired.current.add("complete");
      trackVideo("complete", 100, GULF_VIDEO_LABEL);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[120] flex items-center justify-center p-3 sm:p-6" data-testid="gulf-video-modal" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-[#040914]/95 backdrop-blur-md" onClick={onClose} />
      <div className="relative w-full max-w-3xl max-h-[95vh] overflow-y-auto bg-[#0B1D3A] border border-[#C5A059]/40 rounded-sm shadow-2xl shadow-black/70 fade-up">
        <button onClick={onClose} className="absolute top-3 left-3 z-10 text-white/80 hover:text-white p-2 rounded-full hover:bg-white/5 transition" aria-label="إغلاق" data-testid="gulf-video-close">
          <X className="w-5 h-5" />
        </button>

        <div className="px-5 sm:px-8 pt-9 pb-3 text-center">
          <div className="inline-block text-[#C5A059] text-[10px] sm:text-xs tracking-[0.3em] mb-2">أول سر من الكتاب</div>
          <h3 className="font-display text-lg sm:text-2xl font-black text-white leading-snug">
            استمع إلى أول سر من <span className="text-[#C5A059]">الكتاب الأزرق</span>
          </h3>
        </div>

        <div className="px-3 sm:px-8 py-3">
          <div className="relative w-full bg-black rounded-sm overflow-hidden select-none" style={{ aspectRatio: "16 / 9" }} data-testid="gulf-video-frame" onContextMenu={(e) => e.preventDefault()}>
            <video
              ref={videoRef}
              src={GULF_VIDEO_URL}
              playsInline
              preload="auto"
              controls={false}
              controlsList="nodownload noremoteplayback noplaybackrate"
              disablePictureInPicture
              disableRemotePlayback
              className="w-full h-full"
              onClick={handlePlayPause}
              onPlay={handlePlay}
              onPause={() => setPlaying(false)}
              onEnded={handleEnded}
              onTimeUpdate={handleTimeUpdate}
              onLoadedMetadata={() => setLoading(false)}
              onCanPlay={() => setLoading(false)}
              onWaiting={() => setLoading(true)}
              onPlaying={() => setLoading(false)}
            />
            {!playing && !loading && (
              <button type="button" onClick={handlePlayPause} aria-label="تشغيل" data-testid="gulf-video-play-overlay" className="absolute inset-0 flex items-center justify-center bg-black/40 hover:bg-black/30 transition-colors">
                <span className="w-16 h-16 sm:w-20 sm:h-20 rounded-full bg-[#C5A059] text-[#040914] flex items-center justify-center shadow-[0_0_40px_rgba(197,160,89,0.6)]">
                  <Play className="w-8 h-8 sm:w-10 sm:h-10 mr-1" fill="currentColor" />
                </span>
              </button>
            )}
            {loading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/40 pointer-events-none">
                <Loader2 className="w-10 h-10 text-[#C5A059] animate-spin" />
              </div>
            )}
            <div className="absolute bottom-0 inset-x-0 h-1 bg-white/20 pointer-events-none" data-testid="gulf-video-progress">
              <div className="h-full bg-[#C5A059] transition-[width] duration-150 ease-linear" style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          <div className="mt-3 flex items-center justify-between gap-3" dir="ltr">
            <button type="button" onClick={handlePlayPause} data-testid="gulf-video-playpause" className="flex items-center gap-2 bg-[#C5A059]/15 hover:bg-[#C5A059]/25 border border-[#C5A059]/40 text-[#C5A059] px-4 py-2 rounded-sm text-sm font-bold transition-colors">
              {playing ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              <span>{playing ? "إيقاف مؤقت" : "تشغيل"}</span>
            </button>
            <button type="button" onClick={() => { const v = videoRef.current; if (!v) return; v.muted = !v.muted; setMuted(v.muted); }} data-testid="gulf-video-mute" className="flex items-center gap-2 text-white/80 hover:text-white px-3 py-2 rounded-sm text-sm transition-colors" aria-label={muted ? "تشغيل الصوت" : "كتم الصوت"}>
              {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
            </button>
          </div>
        </div>

        <div className="px-5 sm:px-8 pb-7 pt-2" dir="rtl" data-testid="gulf-video-modal-cta-block">
          <p className="text-center text-sm sm:text-base text-white/90 leading-relaxed mb-4">
            أعجبك ما سمعت؟ النسخة الكاملة تأخذك إلى تفاصيل أعمق <span className="text-[#C5A059] font-bold">+ النسخة الصوتية مجاناً</span>
          </p>
          <Button
            onClick={() => { trackCtaClick(ctaLabel); onBuy?.(); }}
            data-testid="gulf-video-modal-buy-btn"
            className="w-full bg-[#C5A059] text-[#040914] hover:bg-[#E5C07B] hover:text-[#040914] font-black text-base sm:text-lg py-7 rounded-sm shadow-[0_0_40px_rgba(197,160,89,0.4)] cta-pulse"
          >
            <span className="flex flex-col items-center gap-0.5 leading-tight">
              <span>افتح نسختك الآن · {main.primary}</span>
              <span className="text-[12px] sm:text-sm font-semibold text-[#040914]/80">{main.secondary ? `${main.secondary} · ` : ""}استلام فوري · ضمان 7 أيام</span>
            </span>
          </Button>
          <p className="mt-3 text-center text-[11px] text-[#A1B0C6]">دفع آمن ومشفّر · الكتاب الرقمي + النسخة الصوتية</p>
        </div>
      </div>
    </div>
  );
}
